import mixpanel from "mixpanel-browser";
import { useEffect, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { track } from "analytics";
import Loader from 'elements/Loader'
import MainContentContainer from "components/shareable/MainContentContainer";
import { NavbarContext } from "./Data/NavbarProvider";
import TopBar from 'components/shareable/TopBar/TopBar';
import useStyles from "./styles";
import * as Sentry from "@sentry/react";
import { AxiosError } from "axios";
import UpgradeToProSucceededModal from 'components/shareable/UpgradeToProSucceededModal/UpgradeToProSucceededModal';
import useBrand, { BrandExistsState } from 'Hooks/useBrand';
import { getCampaign_api } from "api/campaignApi";
import useUser from "Hooks/useUser";
import { CampaignDetails } from "modules/Types";
import { AdPlatform, CampaignType, PlaybookType } from "modules/Const";
import ButtonSymphony from "elements/ButtonSymphony";
// On this page, we should be getting campaign_id + session_id variables
//  - if both are present, get the campaign details + stripe details
//      - if stripe transaction was a success, show success state
//      - else show error state
//  - if one or the other is missing, show an error state

interface MarketingSuccessPageProps {
    campaignId?: number;
}

const MarketingSuccessPage = ({
    campaignId
}: MarketingSuccessPageProps) => {
    const { currentUser } = useUser();
    const {
        currentBrand,
        getBrands
    } = useBrand() as BrandExistsState

    const history = useHistory()
    const classes = useStyles()
    const { setNextButtonHandler } = useContext(NavbarContext);
    const { getUser } = useUser();

    const [loadingData, setLoadingData] = useState<boolean>(true)
    const [campaignData, setCampaignData] = useState<CampaignDetails | null>(null)
    const [errorState, setErrorState] = useState<boolean>(false)
    const [processingState, setProcessingState] = useState<Boolean>()
    const [sessionId, setSessionId] = useState<string>("")
    const [openWelcomeProModal, setOpenWelcomeProModal] = useState<boolean>(false)
    const metaData = campaignData?.campaign_metadata
    /** Get URL parameters and campaign ID from either props or URL */
    const urlParams = new URLSearchParams(window.location.search);
    const urlCampaignId = urlParams.get('campaign_id')
    const session_id = urlParams.get('session_id')
    const welcome_pro = urlParams.get('welcome_pro')

    /** Use passed in campaignId prop if available, otherwise use URL parameter */
    const campaign_id = campaignId || urlCampaignId

    const getCampaignStatus = async () => {
        // For direct campaign ID access, we don't need session_id validation
        const isCampaignIdProp = Boolean(campaignId)
        const hasRequiredParams = isCampaignIdProp || (campaign_id && session_id)

        if (!hasRequiredParams) {
            setErrorState(true)
            return
        }

        if (welcome_pro && session_id) {
            setOpenWelcomeProModal(true)
            setSessionId(session_id)
        }

        try {
            const finalizeCampaign = await getCampaign_api(Number(campaign_id))
            setCampaignData(finalizeCampaign)
            await getUser(currentUser?.userid)
            await getBrands()
            setLoadingData(false)
        } catch (e) {
            const campaign_type = (e as AxiosError).response?.data?.campaign_metadata?.campaign_type
            console.error('error setting up campaign', e)
            Sentry.captureException(e)
            track('Error Finalizing Campaign', {
                campaign_type,
                brand: currentBrand,
                campaign_id,
                session_id,
                error: e
            })
            await isCampaignProcessing()
        }
    }

    const onErrorStateHandler = () => {
        history.replace('/marketing')
    }

    useEffect(() => {
        getCampaignStatus()
    }, [])

    useEffect(() => {
        const tabName = 'success-page'
        const buttonConfig = {
            tab: tabName,
            show: true,
            text: 'See Your Campaign',
            handler: () => {
                history.push(`/marketing/campaigns/${campaignId}`)
            },
            disabled: false
        }

        if (loadingData) {
            buttonConfig.show = false
            buttonConfig.text = ''
            buttonConfig.handler = () => { }
            buttonConfig.disabled = true
        } else if (errorState) {
            buttonConfig.text = 'Okay'
            buttonConfig.handler = onErrorStateHandler
        }

        setNextButtonHandler(buttonConfig)
    }, [loadingData, errorState])

    /** Tracks campaign submission events in mixpanel */
    useEffect(() => {
        if (!campaignData?.campaign_metadata || !campaignData?.campaign_metadata?.campaign_type) {
            return
        }

        const { campaign_type } = campaignData?.campaign_metadata

        mixpanel.people.increment({
            'submitted_marketing_campaign': 1,
            [`submitted_${campaign_type}_campaign`]: 1
        })

        track('Submit Campaign',
            {
                playbookType: campaign_type,
                adPlatform: AdPlatform.PLAYLISTS,
                brand_id: currentBrand.id,
                brand_name: currentBrand.name,
                brand_slug: currentBrand.slug,
                from: 'Marketing Success Page',
            }
        )
    }, [campaignData])

    const campaignMessage = () => {
        if (!metaData) {
            return null
        }

        const { campaign_type, content } = metaData
        const messages: Partial<Record<PlaybookType | CampaignType, string>> = {
            [PlaybookType.GET_PLAYLISTED]: `Your campaign to pitch "${content.name}" to playlists has been submitted. Once approved, you will see campaign updates in the dashboard.`,
            [PlaybookType.INCREASE_VIDEO_VIEWS]: `Your YouTube ads campaign for "${content.name}" has been submitted. Once it goes live, you will see campaign updates in the dashboard.`,
            [CampaignType.RECORD_STREAMS]: `Your campaign "${content.name}" has been submitted. Once approved, you will see campaign updates in the dashboard.`,
            [PlaybookType.GROW_PLAYLIST_FOLLOWERS]: `Your campaign "${content.name}" has been submitted. Once approved, you will see campaign updates in the dashboard.`,
            [CampaignType.LINK_CLICKS]: 'Your campaign to increase link clicks has been submitted. Once approved by Instagram, it will start running automatically.',
            [CampaignType.BOOST_INSTAGRAM]: 'Your campaign to boost your Instagram engagement has been submitted. Once approved by Instagram, it will start running automatically.'
        }

        return <p className="">{messages[campaign_type]}</p>
    }

    const campaignTitle = () => {
        if (!metaData) {
            return null
        }

        const { campaign_type } = metaData
        return 'Your campaign has been pushed.'
    }

    const isCampaignProcessing = async () => {
        try {
            setTimeout(async () => {
                const campaign = await getCampaign_api(Number(campaign_id))
                if (campaign?.status === 'PROCESSING') {
                    setCampaignData(campaign)
                    setProcessingState(true)
                } else {
                    setErrorState(true)
                }
                setLoadingData(false)
            }, 5000)
        } catch (e) {
            console.error('error getting campaign data', e)
            Sentry.captureException(e)
        }
    }

    const getErrorText = () => {
        if (!metaData) {
            return 'We encountered an error while submitting your campaign. Please try again.'
        }

        /** Maps campaign types to specific error messages */
        const errorMessages: Partial<Record<PlaybookType | CampaignType, string>> = {
            [PlaybookType.GET_PLAYLISTED]: 'We encountered an error while creating your playlist pitching campaign. Please try again.',
            [PlaybookType.INCREASE_VIDEO_VIEWS]: 'We encountered an error while creating your YouTube ads campaign. Please try again.',
            [CampaignType.RECORD_STREAMS]: 'We encountered an error while creating your music streams campaign. Please try again.',
            [PlaybookType.GROW_PLAYLIST_FOLLOWERS]: 'We encountered an error while creating your playlist growth campaign. Please try again.',
            [CampaignType.LINK_CLICKS]: 'We encountered an error while creating your link clicks campaign. Please try again.',
            [CampaignType.BOOST_INSTAGRAM]: 'We encountered an error while creating your Instagram engagement campaign. Please try again.',
        }

        return errorMessages[metaData.campaign_type]
    }
    const getPageContent = () => {
        /** Handles navigation after campaign creation */
        const handleCampaignNavigation = () => {
            const campaignTypes = ['boost_instagram', 'get_playlisted', 'increase_video_views', 'record_streams', 'link_clicks', 'grow_playlist_followers']
            const isCampaignType = campaignTypes.includes(metaData?.campaign_type as string)

            if (isCampaignType) {
                history.push(`/marketing/campaigns/${campaignData?.id}`)
            } else {
                history.push('/marketing/tools/creatives')
            }
        }

        /** Handles click for error state */
        const handleErrorClick = () => {
            history.replace('/marketing')
        }

        /** Shared layout component for all states */
        const PageLayout = ({ title, children }: { title: string, children: React.ReactNode }) => (
            <>
                <TopBar
                    className='marketingSuccessPageTopBar'
                    title={title}
                />
                <MainContentContainer className={classes.mainContainer}>
                    {children}
                </MainContentContainer>
            </>
        )

        /** Shared card layout for error, processing and success states */
        const CardLayout = ({ title, children }: { title: string, children: React.ReactNode }) => (
            <PageLayout title={title}>
                <div className="card-body">
                    <div className="card-content bg-white shadow-none lg:shadow-lg">
                        <div className="top-wrapper">
                            <div className="card-head relative">
                                <h4 className="text-center text-dark lg:text-white text-2xl al">
                                    {title}
                                </h4>
                            </div>
                        </div>
                        {children}
                    </div>
                </div>
            </PageLayout>
        )

        /** Shared message card component for states with similar layouts */
        const MessageCard = ({
            title,
            subtitle,
            message,
            buttonText,
            onButtonClick,
            image
        }: {
            title: string
            subtitle?: string
            message?: React.ReactNode
            buttonText?: string
            onButtonClick?: () => void
            image?: string
        }) => (
            <CardLayout title={title}>
                <div className="text-center mx-auto p-10 lg:p-20">
                    {image && (
                        <img
                            alt={image}
                            className="mx-auto m-10"
                            src={require(`assets/images/${image}`).default}
                        />
                    )}
                    {subtitle && <h5 className="font-bold text-xl">{subtitle}</h5>}
                    {message && <p className="">{message}</p>}
                </div>
                {buttonText && onButtonClick && (
                    <div className="cta-wrapper">
                        <div className="text-center pb-4 px-4 lg:px-10">
                            <ButtonSymphony
                                className="successPageButton"
                                onClick={onButtonClick}
                            >
                                {buttonText}
                            </ButtonSymphony>
                        </div>
                    </div>
                )}
            </CardLayout>
        )

        /** Loading state content */
        if (loadingData) {
            const loadingTitle = 'Finalizing your campaign...'
            return (
                <PageLayout title={loadingTitle}>
                    <Loader className="successPageLoader" title={loadingTitle} />
                </PageLayout>
            )
        }

        /** Error state content */
        if (errorState) {
            return (
                <MessageCard
                    title="Campaign Error"
                    subtitle={getErrorText()}
                    message="We apologize for the inconvenience - please try submitting your campaign again."
                    buttonText="Okay"
                    onButtonClick={handleErrorClick}
                />
            )
        }

        /** Processing state content */
        if (processingState) {
            return (
                <MessageCard
                    title="Campaign is processing"
                    subtitle="Your campaign is being processed. Please check back later"
                    message="Your campaign has been created and is currently being processed."
                    buttonText="See Your Campaign"
                    onButtonClick={handleCampaignNavigation}
                />
            )
        }

        /** Success state content */
        return (
            <MessageCard
                title="Campaign created!"
                subtitle={campaignTitle() || ''}
                message={campaignMessage() || ''}
                buttonText="See Your Campaign"
                onButtonClick={handleCampaignNavigation}
                image="tick-light.png"
            />
        )
    }

    return (
        <>
            <UpgradeToProSucceededModal
                dialogProps={{
                    open: openWelcomeProModal,
                    onClose: () => setOpenWelcomeProModal(false),
                }}
                sessionId={sessionId}
            />
            {getPageContent()}
        </>
    )
}

export default MarketingSuccessPage;